import React, { useContext, useCallback, useMemo } from 'react';
import { Tag } from 'types';
import { i18n } from 'utils/setupTranslations';
import { neverop } from 'utils/noop';
import { FiltersParams } from 'types/params';
import useApiFilters from './hooks/useApiFilters';
import { ApiFilter, ApiFiltersContextType } from './types';

export const DefaultApiFiltersContext = {
    getFilterDisplayName: neverop('ApiFiltersContext', 'getDisplayName', ''),
    apiFilters: [] as ApiFilter[],
};

export const ApiFiltersContext = React.createContext<ApiFiltersContextType>(
    DefaultApiFiltersContext,
);

export const useApiFiltersContext = (): ApiFiltersContextType =>
    useContext(ApiFiltersContext);

export const ApiFiltersProvider = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    const { apiFilters } = useApiFilters();

    const tagsDisplayName = useMemo(() => {
        const tagsList = apiFilters?.find(({ name }) => name === 'Tags')
            ?.list as Tag[];

        if (!tagsList) {
            return {};
        }

        const tagsDisplayName = tagsList
            .map(({ code, displayName }) => [code, displayName])
            .reduce(
                (
                    acc: {
                        [key: string]: string;
                    },
                    [key, value],
                ) => {
                    acc[key] = value;
                    return acc;
                },
                {},
            );

        return tagsDisplayName;
    }, [apiFilters]);

    const getTagDisplayName = useCallback(
        (filter: string) => {
            const i18nFilter = i18n._(filter);
            const translationExist = i18nFilter !== filter;

            return translationExist ? i18nFilter : tagsDisplayName[filter];
        },
        [tagsDisplayName],
    );

    const getFilterDisplayName = useCallback(
        (filter: string, params: FiltersParams) =>
            params === 'tags' ? getTagDisplayName(filter) : i18n._(filter),
        [getTagDisplayName],
    );

    const context = {
        getFilterDisplayName,
        apiFilters,
    };

    return (
        <ApiFiltersContext.Provider value={context}>
            {children}
        </ApiFiltersContext.Provider>
    );
};

export default ApiFiltersProvider;
